/**
 * Bestätigungsdialog statt window.confirm().
 *
 * Nutzung — ohne Provider, als Promise:
 *
 *   import { bestaetigen } from "./ui/Bestaetigen";
 *
 *   if (!(await bestaetigen({
 *     titel: "Regel löschen?",
 *     text: "Die Regel greift danach bei keiner neuen Mail mehr.",   // optional, string oder JSX
 *     bestaetigen: "Löschen",                                        // Beschriftung, Standard „OK"
 *     abbrechen: "Abbrechen",                                        // Standard „Abbrechen"
 *     gefahr: true,                                                  // roter Knopf, Fokus startet auf „Abbrechen"
 *   }))) return;
 *
 * Liefert true bei Bestätigung, false bei Abbrechen, Esc oder Klick daneben.
 * Enter bestätigt, Tab bleibt im Dialog, der Fokus kehrt danach dorthin zurück,
 * wo er vorher war. Mehrere Aufrufe werden nacheinander gezeigt.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { useDialogFokus } from "../../lib/dialog";

export interface BestaetigenOptionen {
  titel: string;
  text?: ReactNode;
  bestaetigen?: string;
  abbrechen?: string;
  gefahr?: boolean;
}

interface Anfrage extends BestaetigenOptionen {
  id: number;
  antwort: (ok: boolean) => void;
}

let warteschlange: Anfrage[] = [];
let naechsteId = 1;
const hoerer = new Set<(a: Anfrage[]) => void>();
let montiert = false;

function melden() {
  for (const h of hoerer) h(warteschlange);
}

function montieren() {
  if (montiert || typeof document === "undefined") return;
  montiert = true;
  const el = document.createElement("div");
  el.dataset.bestaetigenHost = "";
  document.body.appendChild(el);
  createRoot(el).render(<BestaetigenHost />);
}

export function bestaetigen(o: BestaetigenOptionen): Promise<boolean> {
  montieren();
  return new Promise<boolean>((resolve) => {
    const anfrage: Anfrage = {
      ...o,
      id: naechsteId++,
      antwort: (ok) => {
        warteschlange = warteschlange.filter((a) => a.id !== anfrage.id);
        melden();
        resolve(ok);
      },
    };
    warteschlange = [...warteschlange, anfrage];
    melden();
  });
}

function BestaetigenHost() {
  const [liste, setListe] = useState<Anfrage[]>(warteschlange);
  useEffect(() => {
    hoerer.add(setListe);
    return () => { hoerer.delete(setListe); };
  }, []);
  const aktuell = liste[0];
  return aktuell ? <Dialog key={aktuell.id} anfrage={aktuell} /> : null;
}

function Dialog({ anfrage }: { anfrage: Anfrage }) {
  const box = useRef<HTMLDivElement | null>(null);
  const ok = useRef<HTMLButtonElement | null>(null);
  const nein = useRef<HTMLButtonElement | null>(null);
  // Mousedown auf dem Hintergrund merken: Wer im Dialog Text markiert und
  // daneben loslässt, will ihn nicht schließen.
  const druckDaneben = useRef(false);

  useDialogFokus(box, () => anfrage.antwort(false), anfrage.gefahr ? nein : ok);

  return (
    <div
      className="sheet-backdrop bestaetigen-backdrop"
      onMouseDown={(e) => { druckDaneben.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (druckDaneben.current && e.target === e.currentTarget) anfrage.antwort(false); }}
    >
      <div
        ref={box}
        className="sheet bestaetigen"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`bestaetigen-${anfrage.id}-titel`}
        aria-describedby={anfrage.text ? `bestaetigen-${anfrage.id}-text` : undefined}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.nativeEvent.isComposing && document.activeElement !== nein.current) {
            e.preventDefault();
            anfrage.antwort(true);
          }
        }}
      >
        <div className="bestaetigen-inhalt">
          <h2 id={`bestaetigen-${anfrage.id}-titel`}>{anfrage.titel}</h2>
          {anfrage.text && <div id={`bestaetigen-${anfrage.id}-text`} className="bestaetigen-text">{anfrage.text}</div>}
        </div>
        <div className="sheet-foot bestaetigen-foot">
          <div className="spacer" />
          <button ref={nein} type="button" className="btn" onClick={() => anfrage.antwort(false)}>
            {anfrage.abbrechen ?? "Abbrechen"}
          </button>
          <button
            ref={ok}
            type="button"
            className={anfrage.gefahr ? "btn danger-solid" : "btn primary"}
            onClick={() => anfrage.antwort(true)}
          >
            {anfrage.bestaetigen ?? "OK"}
          </button>
        </div>
      </div>
    </div>
  );
}
